import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as api from '@/features/backlog/api';
import { MentionComment } from '@/features/backlog/types';
import { taskPath } from '@/constants/routes';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { useDocumentTitle } from '@/utils/use-document-title';
import { MyCommentRow } from '@/components/my-comments-panel';

/** Comments per page — kept small so each page stays snappy (offset paging walks the full history). */
const PAGE_SIZE = 30;

/** The dedicated, paginated "My comments" page — every comment I've written, newest first. */
export default function MyCommentsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useIdentity();
  const { members, boards } = useAppData();
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const boardById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);
  const [list, setList] = useState<MentionComment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useDocumentTitle('My comments');

  const pageParam = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (!me) return;
    setLoading(true);
    api
      .getMyComments(PAGE_SIZE, (pageParam - 1) * PAGE_SIZE)
      .then((r) => { setList(r.data); setTotal(r.total); })
      .catch(() => { setList([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [me, pageParam]);

  // If the page fell past the end (e.g. comments were deleted), snap back to the last page.
  useEffect(() => {
    if (total > 0 && pageParam > pageCount) {
      setParams((prev) => { const p = new URLSearchParams(prev); p.set('page', String(pageCount)); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, pageParam, pageCount]);

  const setPage = (n: number) => {
    setParams((prev) => { const p = new URLSearchParams(prev); p.set('page', String(Math.max(1, n))); return p; });
    window.scrollTo({ top: 0 });
  };

  const goTo = (c: MentionComment) => {
    const b = boardById.get(c.board_id);
    if (b && c.item_key) navigate(taskPath(b.slug, c.item_key) + `?hl=comment:${c.id}`);
  };

  if (!me) return null;

  return (
    <div className="mx-auto w-full max-w-[860px]">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-[18px] font-semibold text-fg-strong">My comments</h1>
        {total > 0 ? <span className="text-[12px] text-fg-faint">{total} total</span> : null}
      </div>

      {loading && list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line px-6 py-16 text-center text-[13px] text-fg-faint">Loading…</div>
      ) : list.length === 0 && pageParam <= pageCount ? (
        <div className="rounded-2xl border border-dashed border-line px-6 py-16 text-center text-[13px] text-fg-faint">You haven&apos;t commented on anything yet.</div>
      ) : (
        <div className="divide-y divide-line overflow-hidden rounded-2xl border border-line">
          {list.map((c) => (
            <MyCommentRow key={c.id} c={c} onGo={() => goTo(c)} memberById={memberById} meId={me.id} boardName={boardById.get(c.board_id)?.name} />
          ))}
        </div>
      )}

      {total > 0 && pageCount > 1 && pageParam <= pageCount ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[12px] text-fg-faint">
          <span>{loading ? 'Loading…' : `Showing ${(pageParam - 1) * PAGE_SIZE + 1}–${Math.min(pageParam * PAGE_SIZE, total)} of ${total}`}</span>
          <span className="flex items-center gap-1">
            <button type="button" disabled={pageParam <= 1} onClick={() => setPage(pageParam - 1)} className="rounded-lg border border-line px-2.5 py-1 font-medium text-fg-soft transition-colors hover:bg-layer disabled:cursor-not-allowed disabled:opacity-40">Prev</button>
            <span className="px-1.5 tnum text-fg-soft">Page {pageParam} / {pageCount}</span>
            <button type="button" disabled={pageParam >= pageCount} onClick={() => setPage(pageParam + 1)} className="rounded-lg border border-line px-2.5 py-1 font-medium text-fg-soft transition-colors hover:bg-layer disabled:cursor-not-allowed disabled:opacity-40">Next</button>
          </span>
        </div>
      ) : null}
    </div>
  );
}
